import {
  AutoAwesomeOutlined,
  CancelOutlined,
  CheckCircleOutlined,
} from '@mui/icons-material';
import { Divider, Card, Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { VehiclePhotoDamageData } from '@ltvco/refresh-lib/api';
import {
  formatCurrencyRange,
  formatPercent,
  formatCurrencyWithSign,
  getSeverityColor,
  getPostRepairValueColor,
  getNetGainColor,
} from './utils';
import { StyledImage } from './components';

interface DamageAnalyzerResultCardProps {
  estimate?: VehiclePhotoDamageData | null;
  imageToAnalyze?: string | null;
}

export const DamageAnalyzerResultCard = ({
  estimate,
  imageToAnalyze,
}: DamageAnalyzerResultCardProps) => {
  const theme = useTheme();

  if (!estimate) {
    return null;
  }

  const {
    damage_category,
    justification,
    severity,
    repair_type,
    total_min,
    total_max,
    condition_impact,
    value_change,
    repair_recommended,
  } = estimate;

  const totalEstimatedCost = formatCurrencyRange(total_min, total_max);
  const conditionImpact = formatPercent(condition_impact);
  const postRepairValue = formatCurrencyWithSign(value_change);

  /**
   * We already have net_gain_min and net_gain_max in the estimate,
   * but those are not always accurate, so we calculate it manually based on the value change and the total estimated cost
   * and once we have a robust mechanism in the backend to define the endpoint response, we can remove this logic.
   */
  const netGainAfterRepairMin = (value_change ?? 0) - (total_min ?? 0);
  const netGainAfterRepairMax = (value_change ?? 0) - (total_max ?? 0);
  const netGainAfterRepair = formatCurrencyRange(
    netGainAfterRepairMin,
    netGainAfterRepairMax,
  );
  const hasDamage = Number(total_min ?? 0) > 0;
  const severityColor = getSeverityColor(severity);
  const postRepairValueColor = getPostRepairValueColor(value_change);
  const netGainColor = getNetGainColor(
    netGainAfterRepairMin,
    netGainAfterRepairMax,
  );
  const repairIcon = repair_recommended ? (
    <CheckCircleOutlined sx={{ color: theme.palette.success.main }} />
  ) : (
    <CancelOutlined sx={{ color: theme.palette.error.main }} />
  );

  return (
    <Card variant="outlined" sx={{ width: '100%', mt: 1 }}>
      <Stack direction="row" gap={1}>
        <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
        <Stack direction="column" alignItems="start" gap={1} width="100%">
          <Stack direction="row" alignItems="flex-start" gap={1} width="100%">
            <Stack>
              <Text variant="h5">{damage_category || 'Damage analysis'}</Text>
              {justification && (
                <Text variant="body1" color="text.secondary">
                  {justification}
                </Text>
              )}
            </Stack>
            {imageToAnalyze && (
              <StyledImage
                src={`data:image/jpeg;base64,${imageToAnalyze}`}
                alt="Damage analysis"
              />
            )}
          </Stack>
          <Stack direction="column" alignItems="start" gap={1} width="100%">
            {hasDamage && (
              <>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Text variant="body1" color="text.secondary">
                    Severity:{' '}
                  </Text>
                  <Text variant="body1" fontWeight="bold" color={severityColor}>
                    {severity || 'Unknown'}
                  </Text>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Text variant="body1" color="text.secondary">
                    Repair type:
                  </Text>
                  <Text
                    variant="body1"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {repair_type || 'Not specified'}
                  </Text>
                </Stack>
              </>
            )}
            {totalEstimatedCost && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                bgcolor="error.light"
                p={1}
                borderRadius={1}
                width="100%"
              >
                <Text variant="h1" mb={0} color="error.main">
                  {totalEstimatedCost}
                </Text>
                <Text variant="body1" color="error.main">
                  Total estimated repair cost
                </Text>
              </Stack>
            )}
            {conditionImpact && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                width="100%"
              >
                <Text variant="body1" color="text.secondary">
                  Current condition impact
                </Text>
                <Text variant="body1">{conditionImpact}</Text>
              </Stack>
            )}
            {postRepairValue && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                width="100%"
              >
                <Text variant="body1" color="text.secondary">
                  Post-repair value change
                </Text>
                <Text
                  variant="body1"
                  fontWeight="bold"
                  color={postRepairValueColor}
                >
                  {postRepairValue}
                </Text>
              </Stack>
            )}
            {netGainAfterRepair && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                width="100%"
              >
                <Text variant="body1" color="text.secondary">
                  Net gain after repair
                </Text>
                <Text variant="body1" fontWeight="bold" color={netGainColor}>
                  {netGainAfterRepair}
                </Text>
              </Stack>
            )}
            {hasDamage && (
              <>
                <Divider sx={{ marginY: 1.5, width: '100%' }} />
                <Stack
                  direction="row"
                  alignItems="start"
                  justifyContent="flex-start"
                  gap={1}
                  width="100%"
                >
                  {repairIcon}
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    gap={1}
                    pt={1}
                  >
                    <Text
                      variant="body1"
                      fontWeight="bold"
                      color={repair_recommended ? 'success.main' : 'error.main'}
                    >
                      {repair_recommended
                        ? 'Repair recommended'
                        : 'Repair not recommended'}
                    </Text>
                    <Text variant="body1">
                      {repair_recommended
                        ? "Repairs are expected to increase the vehicle's value more than they cost"
                        : "Repairs may not increase the vehicle's value enough to offset their cost"}
                    </Text>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
